import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Activity, Flame, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Kullanıcının kendi günlük öğrenme aktivitesi — son 90 gün.
 *
 * `mode='daily'` günlük yığın bar; `mode='cumulative'` toplam alan grafiği.
 * Default cumulative — bireysel ilerleme algısını güçlendirir.
 */
export default function ActivityTimelineChart({ days = 90, mode = 'cumulative', className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/user/activity-timeline?days=${days}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setData({ error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center text-muted-foreground text-sm">Yükleniyor...</CardContent>
      </Card>
    );
  }

  if (!data || data.error) {
    return null;
  }

  const series = (data.timeline || []).map(b => ({
    date: b.date,
    label: new Date(b.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
    Dersler: mode === 'cumulative' ? b.cumulative_lessons : b.lessons_completed,
    Quizler: mode === 'cumulative' ? b.cumulative_quizzes : b.quizzes_passed,
  }));

  return (
    <Card className={className} data-testid="activity-timeline-chart">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <Activity className="w-4 h-4" />
          Öğrenme Aktivitem
          <Badge variant="outline" className="text-[10px]">son {data.days} gün</Badge>
          <div className="ml-auto flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-orange-500">
              <Flame className="w-3.5 h-3.5" />{data.current_streak} gün streak
            </span>
            <span className="flex items-center gap-1 text-amber-500">
              <Award className="w-3.5 h-3.5" />{data.total_xp} XP
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
              <defs>
                <linearGradient id="colorLessons" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorQuizzes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} interval={Math.floor(series.length / 8)} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} width={32} />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="Dersler" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorLessons)" strokeWidth={2} />
              <Area type="monotone" dataKey="Quizler" stroke="#f59e0b" fillOpacity={1} fill="url(#colorQuizzes)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          {mode === 'cumulative' ? 'Kümülatif toplam — büyüyen eğri ilerlemeni gösterir.' : 'Günlük tamamlananlar.'}
        </p>
      </CardContent>
    </Card>
  );
}
